/**
 * Parents Page
 *
 * Страница со списком родителей.
 * Поиск, пагинация, модалки просмотра и создания.
 * По паттерну Students.js.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Title, Text, Loader, Center, Stack, Box, Group, Pagination, Select, Button,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import ParentsTable from '../components/Parents/ParentsTable';
import ParentViewModal from '../components/Parents/ParentViewModal';
import ParentFormModal from '../components/Parents/ParentFormModal';
import tokens from '../theme/designTokens';
import { useSessionState } from '../hooks/useSessionState';
import { getParents } from '../services/api/parentsService';

const DEFAULT_FILTERS = {
  is_active: new Set(),
};

const filtersSerialize = (filters) => {
  const obj = {};
  Object.entries(filters).forEach(([k, v]) => {
    obj[k] = v instanceof Set ? [...v] : [];
  });
  return obj;
};

const filtersDeserialize = (raw) => {
  const obj = {};
  Object.keys(DEFAULT_FILTERS).forEach((k) => {
    obj[k] = new Set(Array.isArray(raw[k]) ? raw[k] : []);
  });
  return obj;
};

const Parents = () => {
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useSessionState('parents:page', 1);
  const [pageSize, setPageSize] = useSessionState('parents:pageSize', 50);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [searchValue, setSearchValue] = useSessionState('parents:search', '');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  const [sortBy, setSortBy] = useSessionState('parents:sortBy', null);
  const [sortOrder, setSortOrder] = useSessionState('parents:sortOrder', 'asc');

  const filtersOptions = useMemo(() => ({
    serialize: filtersSerialize,
    deserialize: filtersDeserialize,
  }), []);
  const [filters, setFilters] = useSessionState('parents:filters', DEFAULT_FILTERS, filtersOptions);

  // Modals
  const [viewModalOpened, setViewModalOpened] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [formModalOpened, setFormModalOpened] = useState(false);
  const [editParent, setEditParent] = useState(null);

  const abortControllerRef = useRef(null);

  const fetchParents = useCallback(async () => {
    const hasNoneFilter = Object.values(filters).some((f) => f && f.has('__none__'));
    if (hasNoneFilter) {
      setParents([]); setTotal(0); setTotalPages(1); setLoading(false);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      let isActive = null;
      const activeFilter = filters.is_active;
      if (activeFilter && activeFilter.size > 0 && !activeFilter.has('__none__')) {
        if (activeFilter.has('true') && !activeFilter.has('false')) isActive = true;
        else if (activeFilter.has('false') && !activeFilter.has('true')) isActive = false;
      }

      const data = await getParents({
        page,
        pageSize,
        search: debouncedSearch.trim() || null,
        isActive,
        signal: controller.signal,
      });

      setParents(data.items);
      setTotal(data.total);
      setTotalPages(data.pages);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching parents:', err);
      setError(err.message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, filters]);

  useEffect(() => { fetchParents(); }, [fetchParents]);

  const handleFiltersChange = (newFilters) => { setFilters(newFilters); setPage(1); };
  const handleSortChange = (field, order) => { setSortBy(field); setSortOrder(order); setPage(1); };
  const handleSearchChange = (value) => { setSearchValue(value); setPage(1); };
  const handlePageSizeChange = (value) => { setPageSize(Number(value)); setPage(1); };

  const handleRowClick = (parent) => {
    setSelectedParent(parent);
    setViewModalOpened(true);
  };

  const handleEdit = (parent) => {
    setEditParent(parent);
    setViewModalOpened(false);
    setFormModalOpened(true);
  };

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Родители
            </Title>
            <Text c="dimmed" size="sm">Всего: {total}</Text>
          </Box>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => { setEditParent(null); setFormModalOpened(true); }}
          >
            Добавить родителя
          </Button>
        </Group>

        {error ? (
          <Center p="xl"><Text c="red">{error}</Text></Center>
        ) : (
          <>
            <ParentsTable
              data={parents}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onSearchChange={handleSearchChange}
              onSortChange={handleSortChange}
              searchValue={searchValue}
              onRowClick={handleRowClick}
            />

            <Group justify="space-between" mt="md">
              <Group gap="xs">
                <Text size="sm" c="dimmed">Показывать:</Text>
                <Select
                  value={pageSize.toString()}
                  onChange={handlePageSizeChange}
                  data={[
                    { value: '25', label: '25' },
                    { value: '50', label: '50' },
                    { value: '100', label: '100' },
                  ]}
                  size="xs"
                  style={{ width: 80 }}
                />
              </Group>
              {totalPages > 1 && (
                <Pagination total={totalPages} value={page} onChange={setPage} size="sm" siblings={1} boundaries={1} />
              )}
              <Text size="sm" c="dimmed">Страница {page} из {totalPages}</Text>
            </Group>

            {loading && parents.length > 0 && <Center><Loader size="sm" /></Center>}
          </>
        )}

        {loading && parents.length === 0 && !error && (
          <Center p="xl"><Loader size="lg" /></Center>
        )}
      </Stack>

      <ParentViewModal
        opened={viewModalOpened}
        onClose={() => { setViewModalOpened(false); setSelectedParent(null); }}
        parent={selectedParent}
        onEdit={handleEdit}
        onDataChange={fetchParents}
      />

      <ParentFormModal
        opened={formModalOpened}
        onClose={() => { setFormModalOpened(false); setEditParent(null); }}
        parent={editParent}
        onSuccess={fetchParents}
      />
    </Box>
  );
};

export default Parents;
